// ── Client-side acorn pass: instant feedback only ────────────────────────
// Never authoritative and never blocks Apply: the server's /expr-preview
// answer wins. acorn is injected (lazy-loaded by the editor) so this module
// stays importable from a plain node script.
//
// The wrapper "(\n" + src + "\n)" mirrors the server's analyzer
// (code_expr/analyze.py) so positions line up and `1) + (2` is rejected.

const OFFSET = 2;

export function loadAcorn() {
  return import('acorn');
}

export function analyzeClient(acorn, src) {
  const text = String(src ?? '');
  if (!text.trim()) return { empty: true, ok: false, columns: [], error: null };
  const wrapped = '(\n' + text + '\n)';
  let ast;
  try {
    ast = acorn.parse(wrapped, { ecmaVersion: 2022, preserveParens: true, locations: true });
  } catch (e) {
    const lines = text.split('\n');
    let line = e.loc ? e.loc.line - 1 : 1;
    let col = e.loc ? e.loc.column + 1 : 1;
    if (line > lines.length) { line = lines.length; col = lines[lines.length - 1].length + 1; }
    if (line < 1) { line = 1; col = 1; }
    return {
      empty: false, ok: false, columns: [],
      error: { message: String(e.message).replace(/\s*\(\d+:\d+\)$/, ''), line, col },
    };
  }
  const stmt = ast.body.length === 1 && ast.body[0].type === 'ExpressionStatement' ? ast.body[0].expression : null;
  if (!stmt || stmt.type !== 'ParenthesizedExpression' || stmt.start !== 0 || stmt.end !== wrapped.length) {
    return { empty: false, ok: false, columns: [], error: { message: 'Code mode takes exactly one expression.', line: 1, col: 1 } };
  }
  const columns = [];
  const seen = new Set();
  let dynamicAt = null;
  const add = (n) => { if (!seen.has(n)) { seen.add(n); columns.push(n); } };
  const keyOf = (p) => {
    if (p.type === 'Literal') {
      if (typeof p.value === 'string') return p.value;
      if (typeof p.value === 'number' && Number.isInteger(p.value) && p.value >= 0) return String(p.value);
    }
    if (p.type === 'TemplateLiteral' && p.expressions.length === 0) return p.quasis[0].value.cooked;
    return null;
  };
  const walk = (node) => {
    if (!node || typeof node.type !== 'string') return;
    if (node.type === 'MemberExpression' && node.object.type === 'Identifier' && node.object.name === 'row') {
      if (!node.computed) add(node.property.name);
      else {
        const k = keyOf(node.property);
        if (k === null) { if (dynamicAt === null) dynamicAt = node.property.start - OFFSET; } else add(k);
      }
      return;
    }
    for (const key of Object.keys(node)) {
      if (key === 'type' || key === 'start' || key === 'end' || key === 'loc') continue;
      const v = node[key];
      if (Array.isArray(v)) v.forEach(walk); else if (v && typeof v.type === 'string') walk(v);
    }
  };
  walk(stmt.expression);
  if (dynamicAt !== null) {
    const before = text.slice(0, Math.max(0, dynamicAt));
    const line = before.split('\n').length;
    const col = dynamicAt - (before.lastIndexOf('\n') + 1) + 1;
    return { empty: false, ok: false, columns, error: { message: 'Dynamic column access isn\'t supported: use row.name or row["name"].', line, col } };
  }
  return { empty: false, ok: true, columns, error: null };
}

// ── value_body (function body) ───────────────────────────────────────────
// Wrapped like the server's body analyzer: `(function (row) {\n<src>\n})`.
// A bare `return` is therefore legal. Instant feedback only; the server's
// statement allow-list (no loops, let/var, ...) stays authoritative.
export function analyzeBodyClient(acorn, src) {
  const text = String(src ?? '');
  if (!text.trim()) return { empty: true, ok: false, columns: [], error: null };
  const wrapped = '(function (row) {\n' + text + '\n})';
  let ast;
  try {
    ast = acorn.parse(wrapped, { ecmaVersion: 2022, preserveParens: true, locations: true });
  } catch (e) {
    const lines = text.split('\n');
    let line = e.loc ? e.loc.line - 1 : 1;
    let col = e.loc ? e.loc.column + 1 : 1;
    if (line > lines.length) { line = lines.length; col = lines[lines.length - 1].length + 1; }
    if (line < 1) { line = 1; col = 1; }
    return {
      empty: false, ok: false, columns: [],
      error: { message: String(e.message).replace(/\s*\(\d+:\d+\)$/, ''), line, col },
    };
  }
  const stmt = ast.body.length === 1 && ast.body[0].type === 'ExpressionStatement' ? ast.body[0].expression : null;
  const fn = stmt && stmt.type === 'ParenthesizedExpression' ? stmt.expression : null;
  if (!fn || fn.type !== 'FunctionExpression' || stmt.end !== wrapped.length) {
    return { empty: false, ok: false, columns: [], error: { message: 'The code must be a function body.', line: 1, col: 1 } };
  }
  const columns = [];
  const seen = new Set();
  let dynamicAt = null;
  const add = (n) => { if (!seen.has(n)) { seen.add(n); columns.push(n); } };
  const bodyStart = wrapped.indexOf('\n') + 1;
  const walk = (node) => {
    if (!node || typeof node.type !== 'string') return;
    if (node.type === 'MemberExpression' && node.object.type === 'Identifier' && node.object.name === 'row') {
      if (!node.computed) add(node.property.name);
      else {
        const p = node.property;
        let k = null;
        if (p.type === 'Literal') {
          if (typeof p.value === 'string') k = p.value;
          else if (typeof p.value === 'number' && Number.isInteger(p.value) && p.value >= 0) k = String(p.value);
        } else if (p.type === 'TemplateLiteral' && p.expressions.length === 0) k = p.quasis[0].value.cooked;
        if (k === null) { if (dynamicAt === null) dynamicAt = p.start - bodyStart; } else add(k);
      }
      return;
    }
    for (const key of Object.keys(node)) {
      if (key === 'type' || key === 'start' || key === 'end' || key === 'loc') continue;
      const v = node[key];
      if (Array.isArray(v)) v.forEach(walk); else if (v && typeof v.type === 'string') walk(v);
    }
  };
  walk(fn.body);
  if (dynamicAt !== null) {
    const before = text.slice(0, Math.max(0, dynamicAt));
    const line = before.split('\n').length;
    const col = dynamicAt - (before.lastIndexOf('\n') + 1) + 1;
    return { empty: false, ok: false, columns, error: { message: 'Dynamic column access isn\'t supported: use row.name or row["name"].', line, col } };
  }
  return { empty: false, ok: true, columns, error: null };
}
